import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../../firebase-config';
import './LoginModal.css';

function LoginModal({ onClose }) {
  const navigate = useNavigate();
  const [email, setEmail]           = useState('');
  const [password, setPassword]     = useState('');
  const [error, setError]           = useState('');
  const [loading, setLoading]       = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showReset, setShowReset]   = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetMsg, setResetMsg]     = useState('');

  // Lock body scroll while open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  // Escape to close
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await signInWithEmailAndPassword(auth, email, password);
      onClose();
      navigate('/dashboard');
    } catch (err) {
      if      (err.code === 'auth/user-not-found')     setError('No account found with this email.');
      else if (err.code === 'auth/wrong-password')     setError('Incorrect password.');
      else if (err.code === 'auth/invalid-credential') setError('Invalid email or password.');
      else if (err.code === 'auth/invalid-email')      setError('Invalid email address.');
      else if (err.code === 'auth/too-many-requests')  setError('Too many attempts. Try again later.');
      else                                              setError('Sign-in failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setResetMsg('');
    setError('');
    setResetLoading(true);
    try {
      if (!resetEmail) { setError('Please enter your email address.'); setResetLoading(false); return; }
      await sendPasswordResetEmail(auth, resetEmail);
      setResetMsg('Reset link sent. Check your inbox.');
      setResetEmail('');
      setTimeout(() => { setShowReset(false); setResetMsg(''); }, 3000);
    } catch (err) {
      if (err.code === 'auth/user-not-found') setError('No account found with this email.');
      else                                     setError('Failed to send reset email.');
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="lm-overlay" onClick={onClose}>
      <div className="lm-panel" onClick={(e) => e.stopPropagation()}>

        {/* Animated scan line */}
        <div className="lm-scanline" />

        {/* Close */}
        <button className="lm-close" onClick={onClose} aria-label="Close">✕</button>

        <div className="lm-rule" />
        <div className="lm-eyebrow">Secure Access</div>

        {!showReset ? (
          <>
            <div className="lm-title">Client Dashboard</div>
            <div className="lm-subtitle">Authorized credentials required.</div>

            {error && <div className="lm-error">{error}</div>}

            <form onSubmit={handleSubmit} className="lm-form">
              <div className="lm-field">
                <label htmlFor="lm-email" className="lm-label">Email</label>
                <input
                  type="email" id="lm-email"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com" autoComplete="email"
                  className="lm-input" required
                />
              </div>

              <div className="lm-field">
                <label htmlFor="lm-pass" className="lm-label">Password</label>
                <div className="lm-input-wrap">
                  <input
                    type={showPassword ? 'text' : 'password'} id="lm-pass"
                    value={password} onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter password" autoComplete="current-password"
                    className="lm-input" required
                  />
                  <button
                    type="button" className="lm-eye"
                    onClick={() => setShowPassword(v => !v)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                        <line x1="1" y1="1" x2="23" y2="23"/>
                      </svg>
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <button type="submit" disabled={loading} className="lm-btn">
                {loading ? 'Authenticating...' : 'Access Dashboard →'}
              </button>
            </form>

            <div className="lm-links">
              <button onClick={() => { setShowReset(true); setError(''); }} className="lm-link">
                Forgot password?
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="lm-title">Reset Password</div>
            <div className="lm-subtitle">A secure link will be sent to your inbox.</div>

            {error   && <div className="lm-error">{error}</div>}
            {resetMsg && <div className="lm-success">{resetMsg}</div>}

            <form onSubmit={handlePasswordReset} className="lm-form">
              <div className="lm-field">
                <label htmlFor="lm-reset-email" className="lm-label">Email</label>
                <input
                  type="email" id="lm-reset-email"
                  value={resetEmail} onChange={(e) => setResetEmail(e.target.value)}
                  placeholder="your@email.com" autoComplete="email"
                  className="lm-input" required
                />
              </div>
              <button type="submit" disabled={resetLoading} className="lm-btn">
                {resetLoading ? 'Sending...' : 'Send Reset Link'}
              </button>
            </form>

            <div className="lm-links">
              <button onClick={() => { setShowReset(false); setError(''); }} className="lm-link">
                ← Back to sign in
              </button>
            </div>
          </>
        )}

        <div className="lm-footer">R A M D E S I G N W O R K S — Authorized clients only.</div>

      </div>
    </div>
  );
}

export default LoginModal;
